<template>
    <div id="lx-online-booking-checkout-travel-details" class="lx-online-booking-checkout-travel-details">
        <card
            class="lx-online-booking-checkout-travel-details__card"
            :headerContentClass="['w-100']"
            v-for="(techReservation, index) in reservation.tech_reservations"
            :key="index"
        >
            <template #header-icon>
                <font-awesome-icon :icon="['fas', 'hotel']" size="lg" />
            </template>
            <template #header-content>
                <div class="lx-online-booking-checkout-travel-details__name">
                    {{ techReservation.tech_reservation.property_name }}
                    <span @click="deleteTechReservation(techReservation)">
                        <font-awesome-icon
                            v-if="
                                website.display_remove_button &&
                                reservation.is_editable &&
                                reservation.tech_reservations.length > 1
                            "
                            class="lx-online-booking-checkout-travel-details__name__remove mr-3"
                            :icon="['fas', 'circle-xmark']"
                        ></font-awesome-icon>
                    </span>
                </div>
            </template>
            <template #body>
                <div class="lx-online-booking-checkout-travel-details__reservation">
                    <div class="lx-online-booking-checkout-travel-details__reservation__property">
                        <div class="lx-online-booking-checkout-travel-details__reservation__property__info">
                            <div
                                class="lx-online-booking-checkout-travel-details__reservation__property__info__gallery"
                                v-if="techReservation.property.photos.length !== 0"
                            >
                                <online-booking-checkout-gallery
                                    :galleryId="`gallery-id-${index}`"
                                    :photos="techReservation.property.photos"
                                    :beds="techReservation.property.bedrooms"
                                    :baths="techReservation.property.baths"
                                    :galleryImageNumber="website.gallery_image_number"
                                />
                            </div>
                        </div>
                    </div>
                    <online-booking-checkout-property-details
                        :class="{ 'mt-4': techReservation.property.photos.length !== 0 }"
                        :tech-reservation="techReservation"
                        :is-editable="reservation.is_editable"
                    />
                    <div
                        v-if="techReservation.all_fees.length || techReservation.all_services.length"
                        class="lx-online-booking-checkout-travel-details__reservation__fees-services"
                    >
                        <card :nested="true">
                            <template #header-icon>
                                <font-awesome-icon :icon="['fas', 'hand-holding-dollar']" size="lg" />
                            </template>
                            <template #header-content> Fees & Optional Services </template>
                            <template #body>
                                <div v-for="(fee, feeIndex) in techReservation.all_fees" :key="fee.id">
                                    <div
                                        class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry"
                                        v-if="
                                            (reservation.is_editable || fee.selected) &&
                                            (!fee.is_pet_fee ||
                                                (techReservation.property.is_pets &&
                                                    techReservation.tech_reservation.pets &&
                                                    techReservation.tech_reservation.number_of_pets > 0))
                                        "
                                    >
                                        <div v-if="fee.is_optional">
                                            <form-checkbox
                                                :checked="fee.selected"
                                                @change="
                                                    (val) => setTechReservationFeeSelected(val, fee, techReservation)
                                                "
                                            />
                                        </div>
                                        <font-awesome-icon
                                            v-else-if="!reservation.is_editable || !fee.is_optional"
                                            class="text-success lx-online-booking-checkout-travel-details__reservation__fees-services__entry__icon"
                                            :icon="['far', 'square-check']"
                                        ></font-awesome-icon>
                                        <div
                                            class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__info"
                                        >
                                            <div
                                                class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__info__title"
                                            >
                                                {{ fee.title }}
                                                <span
                                                    v-if="
                                                        fee.is_pet_fee &&
                                                        techReservation.tech_reservation.number_of_pets > 1
                                                    "
                                                >
                                                    ({{ techReservation.tech_reservation.number_of_pets }}
                                                    pets)
                                                </span>
                                            </div>
                                            <div
                                                class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__info__description"
                                            >
                                                {{ fee.description }}
                                            </div>
                                        </div>
                                        <div
                                            class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__value"
                                        >
                                            {{ getFeeValue(fee) }}
                                        </div>
                                    </div>
                                </div>
                                <div v-for="(service, serviceIndex) in techReservation.all_services" :key="service.id">
                                    <div
                                        class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry"
                                        v-if="reservation.is_editable || service.selected"
                                    >
                                        <div v-if="reservation.is_editable">
                                            <form-checkbox
                                                :checked="service.selected"
                                                @change="
                                                    (val) =>
                                                        setTechReservationServiceSelected(val, service, techReservation)
                                                "
                                            />
                                        </div>
                                        <font-awesome-icon
                                            v-else
                                            class="text-success lx-online-booking-checkout-travel-details__reservation__fees-services__entry__icon"
                                            :icon="['far', 'square-check']"
                                        ></font-awesome-icon>
                                        <div
                                            class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__info"
                                        >
                                            <div
                                                class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__info__title"
                                            >
                                                {{ service.name }}
                                            </div>
                                            <div
                                                class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__info__description"
                                            >
                                                {{ service.description }}
                                            </div>
                                        </div>
                                        <div
                                            class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__value"
                                        >
                                            {{ getServiceValue(service) }}
                                            <span
                                                v-if="service.is_multiple && !service.is_percent"
                                                class="d-inline-flex align-items-center"
                                            >
                                                x&nbsp;
                                                <form-input
                                                    class="lx-online-booking-checkout-travel-details__reservation__fees-services__entry__value__count"
                                                    title="How many"
                                                    type="number"
                                                    v-if="reservation.is_editable"
                                                    v-model="service.selected_count"
                                                    @input="setTechReservationServiceCount(techReservation)"
                                                    :disabled="!service.selected"
                                                />
                                                <span v-else>{{ service.selected_count }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </card>
                    </div>
                    <div
                        class="lx-online-booking-checkout-travel-details__reservation__guests"
                        v-if="techReservation.property.is_require_guest_names"
                    >
                        <card :nested="true">
                            <template #header-icon>
                                <font-awesome-icon :icon="['fas', 'users']" size="lg" />
                            </template>
                            <template #header-content> Guests </template>
                            <template #body>
                                <b-row
                                    v-for="(guestName, guestIndex) in getClientGuestNames(techReservation)"
                                    class="lx-online-booking-checkout-travel-details__reservation__guests__guest"
                                    :key="guestIndex"
                                >
                                    <span
                                        class="lx-online-booking-checkout-travel-details__reservation__guests__guest__number"
                                    >
                                        #{{ guestIndex + 1 }}
                                    </span>
                                    <b-col sm="12" md="4" lg="4">
                                        <validation-provider
                                            :name="
                                                's1_' +
                                                'tech_reservations' +
                                                `[${index}].` +
                                                'guest_names' +
                                                `[${guestIndex}].` +
                                                'name'
                                            "
                                            rules="required|alphaSpaces"
                                            v-slot="{ errors, handleChange }"
                                            :value="guestName.name"
                                        >
                                            <form-input
                                                v-model="guestName.name"
                                                floating-label="Name"
                                                :leftIcon="['fas', 'user']"
                                                :error="errors[0]"
                                                @input="handleChange"
                                                :name="
                                                    's1_' +
                                                    'tech_reservations' +
                                                    `[${index}].` +
                                                    'guest_names' +
                                                    `[${guestIndex}].` +
                                                    'name'
                                                "
                                            />
                                        </validation-provider>
                                    </b-col>
                                    <b-col sm="12" md="5" v-if="techReservation.property.is_require_guest_email">
                                        <validation-provider
                                            :name="
                                                's1_' +
                                                'tech_reservations' +
                                                `[${index}].` +
                                                'guest_names' +
                                                `[${guestIndex}].` +
                                                'email'
                                            "
                                            rules="required|email"
                                            type="email"
                                            v-slot="{ errors, handleChange }"
                                            :value="guestName.email"
                                        >
                                            <form-input
                                                v-model="guestName.email"
                                                floating-label="Email"
                                                :leftIcon="['fas', 'at']"
                                                :error="errors[0]"
                                                @input="handleChange"
                                                :name="
                                                    's1_' +
                                                    'tech_reservations' +
                                                    `[${index}].` +
                                                    'guest_names' +
                                                    `[${guestIndex}].` +
                                                    'email'
                                                "
                                            />
                                        </validation-provider>
                                    </b-col>
                                    <b-col sm="12" md="2" v-if="techReservation.property.is_require_guest_age">
                                        <validation-provider
                                            :name="
                                                's1_' +
                                                'tech_reservations' +
                                                `[${index}].` +
                                                'guest_names' +
                                                `[${guestIndex}].` +
                                                'age'
                                            "
                                            rules="required"
                                            v-slot="{ errors, handleChange }"
                                        >
                                            <form-select
                                                v-model="guestName.age"
                                                floating-label="Age"
                                                :options="ageOptions"
                                                :error="errors[0]"
                                                @input="handleChange"
                                                :name="
                                                    's1_' +
                                                    'tech_reservations' +
                                                    `[${index}].` +
                                                    'guest_names' +
                                                    `[${guestIndex}].` +
                                                    'age'
                                                "
                                            />
                                        </validation-provider>
                                    </b-col>
                                </b-row>
                            </template>
                        </card>
                    </div>
                </div>
            </template>
        </card>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import range from 'lodash/range';
import { toAmountDisplay } from '../../../../utils/numbers';
import Card from '../../../generic/card/card.vue';
import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';
import FormInput from '../../../generic/form-input/form-input.vue';
import FormSelect from '../../../generic/form-select/form-select.vue';
import Modal from '../../../generic/modal/modal.vue';
import OnlineBookingCheckoutPropertyDetails from './online-booking-checkout-property-details.vue';
import OnlineBookingCheckoutGallery from './online-booking-checkout-gallery.vue';

export default {
    name: 'OnlineBookingCheckoutTravelDetails',
    components: {
        Card,
        FormCheckbox,
        FormInput,
        FormSelect,
        OnlineBookingCheckoutPropertyDetails,
        Modal,
        OnlineBookingCheckoutGallery,
    },
    computed: {
        ...mapGetters('onlineBooking', ['client', 'reservation', 'website']),
        ageOptions() {
            return [
                { value: null, text: 'Not set' },
                ...range(1, 100).map((age) => ({
                    value: age,
                    text: age,
                })),
            ];
        },
        multipleReservations() {
            return this.reservation.tech_reservations.length > 1;
        },
    },
    methods: {
        getClientGuestNames(techReservation) {
            const clientTechReservation = this.client.tech_reservations.find(
                (tr) => tr.id === techReservation.tech_reservation.id
            );
            if (clientTechReservation) {
                return clientTechReservation.guest_names;
            }
            return [];
        },
        getFeeValue(fee) {
            return toAmountDisplay({
                amount: fee.value,
                currency: this.reservation.currency.symbol,
                percentage: fee.display_percentage,
            });
        },
        getServiceValue(service) {
            return toAmountDisplay({
                amount: service.price,
                currency: this.reservation.currency.symbol,
                percentage: service.is_percent,
            });
        },
        async deleteTechReservation(techReservation) {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                await this.$store.dispatch('onlineBooking/deleteTechReservation', {
                    id: techReservation.tech_reservation.id,
                });
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        async setTechReservationFeeSelected(value, fee, techReservation) {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                await this.$store.dispatch('onlineBooking/updateTechReservation', {
                    id: techReservation.tech_reservation.id,
                    ...techReservation,
                    all_fees: techReservation.all_fees.map((item) => {
                        if (item.id === fee.id) {
                            return {
                                ...item,
                                selected: value,
                            };
                        }
                        return item;
                    }),
                });
            } catch {
                this.errorToast({
                    text: 'Could not save fee.',
                });
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        async setTechReservationServiceSelected(value, service, techReservation) {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                await this.$store.dispatch('onlineBooking/updateTechReservation', {
                    id: techReservation.tech_reservation.id,
                    ...techReservation,
                    all_services: techReservation.all_services.map((item) => {
                        if (item.id === service.id) {
                            return {
                                ...item,
                                selected: value,
                            };
                        }
                        return item;
                    }),
                });
            } catch {
                this.errorToast({
                    text: 'Could not save fee.',
                });
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        setTechReservationServiceCount: debounce(async function (techReservation) {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                await this.$store.dispatch('onlineBooking/updateTechReservation', {
                    id: techReservation.tech_reservation.id,
                    ...techReservation,
                });
            } catch {
                this.errorToast({
                    text: 'Could not save service count.',
                });
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        }, 1000),
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

#lx-online-booking-checkout-travel-details .lx-online-booking-checkout-travel-details {
    &__card {
        margin-bottom: 30px;
    }

    &__name {
        font-size: 17px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        &__remove {
            cursor: pointer;
            font-size: 1.35rem;
        }
    }

    &__reservation {
        &__property {
            @include media-breakpoint-down(md) {
                flex-direction: column;
            }

            &__info {
                display: flex;
                flex-direction: column;

                @include media-breakpoint-down(md) {
                    margin-left: 0;
                }
            }
        }

        &__guests {
            :deep(.card-body) {
                padding-bottom: 0px !important;
            }

            &__guest {
                &__separator {
                    @include media-breakpoint-up(md) {
                        margin-top: 0px !important;
                    }

                    @include media-breakpoint-down(sm) {
                        margin-bottom: 1.5rem !important;
                    }
                }

                &__number {
                    font-weight: bold;
                    margin-top: 0.7rem;
                    margin-left: 0.9rem;

                    @include media-breakpoint-down(sm) {
                        margin-top: 0px;
                        margin-bottom: 0.6rem !important;
                    }
                }
            }
        }

        &__fees-services {
            margin-top: 1.5rem;

            &__title {
                margin-bottom: 1rem;
            }

            &__entry {
                display: grid;
                grid-template-columns: max-content auto max-content;
                margin-bottom: 1.5rem;

                &__icon {
                    font-size: 1.2rem;
                    margin-right: 0.4rem;
                }

                &__info {
                    margin-left: 0.5rem;
                    overflow-wrap: anywhere;

                    &__title {
                        font-weight: bold;
                        line-height: 1;
                    }

                    &__description {
                        font-size: 14px;
                        margin-top: 5px;
                        line-height: 1.5;
                    }
                }

                &__value {
                    padding-left: 2rem;
                    text-align: right;

                    fieldset.form-group {
                        margin-bottom: 0;
                    }

                    &__count {
                        width: 4rem;
                        margin-left: 0.25rem;
                    }
                }
            }
        }
    }
}
</style>
